
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Phone, Lock, AlertCircle } from 'lucide-react'
import { Button, H2, Body, ButtonVariant, Input, INPUT_MASKS } from '@meiuca/ui-components'
import { useAuth } from '@/contexts/AuthContext'
import type { LoginFormData } from '@/types/auth'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents'

const loginSchema = z.object({
  phone: z.string()
    .min(1, 'Telefone é obrigatório')
    .transform((val) => val.replace(/\D/g, '')) // remove máscara
    .refine((val) => val.length >= 10, 'Telefone deve ter pelo menos 10 dígitos')
    .refine((val) => val.length <= 11, 'Telefone deve ter no máximo 11 dígitos')
    .refine((val) => /^[1-9]{2}[2-9][0-9]{7,8}$/.test(val), 'Telefone inválido'),
  password: z.string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres'),
  rememberMe: z.boolean().optional(),
})

interface LoginFormProps {
  onSuccess?: () => void
  onSignUpClick?: () => void
  onForgotPasswordClick?: () => void
}

export function LoginForm({ onSuccess, onSignUpClick, onForgotPasswordClick }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()
  const { login, isLoading, error, clearError } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    try {
      console.log('🔑 LoginForm: Starting login...')

      // Track login attempt
      analyticsService.track(ANALYTICS_EVENTS.LOGIN_ATTEMPT, {
        method: 'password'
      })

      clearError()
      clearErrors()
      await login(data)
      console.log('🔑 LoginForm: Login successful, calling onSuccess')

      // Track login success (CONVERSION!)
      analyticsService.track(ANALYTICS_EVENTS.LOGIN_SUCCESS, {
        method: 'password'
      })

      onSuccess?.()
      console.log('🔑 LoginForm: onSuccess called')
    } catch (error) {
      console.log('🔑 LoginForm: Login failed', error)

      const errorType = error instanceof Error
        ? (/401|não autorizado|incorret|credential/i.test(error.message)
            ? 'invalid_credentials'
            : /network|conex|timeout/i.test(error.message)
              ? 'network'
              : 'unknown')
        : 'unknown'

      // Track login error
      analyticsService.track(ANALYTICS_EVENTS.LOGIN_ERROR, {
        method: 'password',
        error_type: errorType,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      })

      // Error is handled by useAuth hook
    }
  }

  const handleForgotPasswordClick = () => {
    analyticsService.track(ANALYTICS_EVENTS.AUTH_NAV_CLICKED, { target: 'forgot_password' })
    if (onForgotPasswordClick) {
      onForgotPasswordClick()
      return
    }
    navigate('/forgot-password')
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="text-center mb-8">
        <motion.div
          className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-2xl mx-auto mb-6 flex items-center justify-center"
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <span className="text-2xl font-bold text-white">M</span>
        </motion.div>
        
        <H2 className="mb-2">Bem-vindo de volta!</H2>
        <Body className="text-text-secondary">
          Entre na sua conta para continuar
        </Body>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-6 p-4 bg-background-negative border border-red-200 rounded-lg flex items-center gap-3"
        >
          <AlertCircle className="w-5 h-5 text-negative flex-shrink-0" />
          <Body className="text-negative text-sm">{error}</Body>
        </motion.div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Phone Field */}
        <Input
          {...register('phone')}
          type="tel"
          label="Telefone"
          placeholder="(11) 99999-9999"
          mask={INPUT_MASKS.PHONE_BR}
          unmask={true}
          leftIcon={Phone}
          error={!!errors.phone}
          errorMessage={errors.phone?.message}
          fullWidth
          className="w-full"
        />

        {/* Password Field */}
        <Input
          {...register('password')}
          type={showPassword ? 'text' : 'password'}
          label="Senha"
          placeholder="••••••••"
          leftIcon={Lock}
          trailingButton={{
            icon: showPassword ? EyeOff : Eye,
            onClick: () => setShowPassword(!showPassword),
            'aria-label': showPassword ? 'Ocultar senha' : 'Mostrar senha'
          }}
          error={!!errors.password}
          errorMessage={errors.password?.message}
          fullWidth
          className="w-full"
        />

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              {...register('rememberMe')}
              type="checkbox"
              className="w-4 h-4 text-brand-primary border-gray-300 rounded focus:ring-brand-primary focus:ring-2"
            />
            <span className="text-sm text-text-secondary">Lembrar de mim</span>
          </label>
          
          <button
            type="button"
            className="text-sm text-brand-primary hover:text-brand-secondary transition-colors"
            onClick={handleForgotPasswordClick}
          >
            Esqueci minha senha
          </button>
        </div>

        {/* Submit Button */}
        <Button
          variant={ButtonVariant.PRIMARY}
          type="submit"
          loading={isLoading}
          className="w-full"
        >
          {isLoading ? 'Entrando...' : 'Entrar'}
        </Button>
      </form>

      {/* Sign Up Link */}
      <div className="mt-8 text-center">
        <Body className="text-text-secondary">
          Não tem uma conta?{' '}
          <button
            onClick={() => {
              analyticsService.track(ANALYTICS_EVENTS.AUTH_NAV_CLICKED, { target: 'signup' })
              onSignUpClick?.()
            }}
            className="text-brand-primary hover:text-brand-secondary font-medium transition-colors"
          >
            Criar conta
          </button>
        </Body>
      </div>
    </motion.div>
  )
}
